import { createWithEqualityFn } from 'zustand/traditional';

import { MajorListSort, ScholarshipSort } from '@common-types';
import { getStateSpotlight } from '@api/stateSpotlight';
import useAuthStore from '@store/AuthStore';
import useSchoolSearchFiltersStore, { INITIAL_SCHOOL_SORT } from '@store/SchoolSearchFiltersStore';
import useMajorSearchStore, { INITIAL_MAJOR_SORT } from '@store/MajorSearchStore';
import useScholarshipListStore from '@store/AllScholarshipsStore';
import { INITIAL_VALUE as INITIAL_SCHOLARSHIP_SORT } from '@pages/Scholarships/components/SortBy/SortBy';
import { REST_OF_US_FILTER, STATE_PUBLIC_COLLEGE_FILTER, SchoolListSortOption } from '@constants/stateSpotlight';
import { StateSpotlight, StateSpotlightPage } from '@interfaces/stateSpotlight';

export interface StateSpotlightStore {
  spotlight: StateSpotlight | null;
  isLoaded: boolean;
  isLoading: boolean;
  loadedForUserId: string | null;
  appliedDefaults: Record<StateSpotlightPage, boolean>;
  carriedFilters: Record<StateSpotlightPage, boolean>;
  loadSpotlight: () => Promise<void>;
  markDefaultsApplied: (page: StateSpotlightPage) => void;
  markFiltersCarried: (page: StateSpotlightPage) => void;
}

const initialState = {
  spotlight: null,
  isLoaded: false,
  isLoading: false,
  loadedForUserId: null,
  appliedDefaults: {
    schools: false,
    majors: false,
    scholarships: false,
  },
  carriedFilters: {
    schools: false,
    majors: false,
    scholarships: false,
  },
};

const STATE_SPOTLIGHT_FILTERS = [STATE_PUBLIC_COLLEGE_FILTER, REST_OF_US_FILTER] as const;

const clearStateSpotlightFilters = () => {
  const schoolStore = useSchoolSearchFiltersStore.getState();
  const majorStore = useMajorSearchStore.getState();
  const scholarshipStore = useScholarshipListStore.getState();

  STATE_SPOTLIGHT_FILTERS.forEach((key) => {
    if (key in schoolStore.filters) schoolStore.removeSearchFilter(key);
    if (key in majorStore.filters) majorStore.removeSearchFilter(key);
    if (key in scholarshipStore.filters) scholarshipStore.removeScholarshipFilter(key);
  });

  if ([SchoolListSortOption.USHE, SchoolListSortOption.USHE_DISTANCE].includes(schoolStore.sortByValues.sort))
    schoolStore.setSortByValues(INITIAL_SCHOOL_SORT);
  if (majorStore.sortByValues.sort === MajorListSort.NEAREST_UTAH)
    majorStore.setDefaultSortByValues(INITIAL_MAJOR_SORT);
  if (scholarshipStore.sortByValues.sort === ScholarshipSort.UTAH)
    scholarshipStore.setSortByFields(INITIAL_SCHOLARSHIP_SORT);
};

export const selectIsWaitingForDefaults =
  (page: StateSpotlightPage) => (state: Pick<StateSpotlightStore, 'isLoaded' | 'spotlight' | 'appliedDefaults'>) =>
    !state.isLoaded || (!!state.spotlight && !state.appliedDefaults[page]);

export const useStateSpotlightStore = createWithEqualityFn<StateSpotlightStore>()(
  (set, get) => ({
    ...initialState,

    loadSpotlight: async () => {
      const { initializedUser, authSession } = useAuthStore.getState();
      const userId = authSession.userData?.userId ?? null;

      if (!initializedUser) return;

      if (get().isLoading || get().isLoaded) {
        if (get().loadedForUserId === userId) return;

        set({ ...initialState });
      }

      set({ isLoading: true, loadedForUserId: userId });

      let spotlight: StateSpotlight | null = null;

      try {
        const result = await getStateSpotlight();

        spotlight = result.isEnabled ? result : null;
      } catch {
        spotlight = null;
      }

      if (get().loadedForUserId !== userId) return;

      set({ spotlight, isLoaded: true, isLoading: false });

      if (!spotlight) clearStateSpotlightFilters();
    },

    markDefaultsApplied: (page) => {
      set({ appliedDefaults: { ...get().appliedDefaults, [page]: true } });
    },

    markFiltersCarried: (page) => {
      set({ carriedFilters: { ...get().carriedFilters, [page]: true } });
    },
  }),
  Object.is,
);
